/**
 * Custom header name for Ellmo AI to pass pathname information
 * Using 'x-ellmo-pathname' to avoid conflicts with other libraries
 */
export const ELLMO_PATHNAME_HEADER = 'x-ellmo-pathname';

/**
 * Type definitions for Next.js middleware (to avoid requiring Next.js as a dependency)
 */
export interface EllmoRequest {
  nextUrl: {
    pathname: string;
  };
}

export interface EllmoResponse {
  headers: {
    set(name: string, value: string): void;
  };
}

/**
 * Ellmo AI middleware helper for Next.js
 * 
 * This is a vanilla helper that adds pathname to request headers.
 * It doesn't depend on Next.js - you provide the request/response objects.
 * 
 * Usage in Next.js:
 * 
 * ```ts
 * // middleware.ts
 * import { ellmoMiddleware } from 'ellmo-ai-react/middleware';
 * import { NextResponse } from 'next/server';
 * import type { NextRequest } from 'next/server';
 * 
 * export function middleware(request: NextRequest) {
 *   const response = NextResponse.next();
 *   return ellmoMiddleware(request, response);
 * }
 * 
 * export const config = {
 *   matcher: [
 *     '/((?!_next/static|_next/image|favicon.ico).*)',
 *   ],
 * };
 * ```
 * 
 * Composing with existing middleware:
 * 
 * ```ts
 * export function middleware(request: NextRequest) {
 *   let response = NextResponse.next();
 *   
 *   // Your logic here
 *   if (someCondition) {
 *     response = NextResponse.redirect(new URL('/login', request.url));
 *   }
 *   
 *   // Apply Ellmo middleware
 *   return ellmoMiddleware(request, response);
 * }
 * ```
 */
export function ellmoMiddleware<
  Req extends EllmoRequest,
  Res extends EllmoResponse
>(
  request: Req,
  response: Res
): Res {
  // Extract pathname from the request URL
  const pathname = request.nextUrl.pathname;
  
  // Add the pathname to a custom header
  response.headers.set(ELLMO_PATHNAME_HEADER, pathname);
  
  return response;
}

/**
 * Helper to extract pathname from headers object
 * 
 * Usage in Next.js Server Components:
 * ```tsx
 * import { headers } from 'next/headers';
 * import { getPathnameFromHeaders } from 'ellmo-ai-react/middleware';
 * 
 * export default function Page() {
 *   const headersList = headers();
 *   const pathname = getPathnameFromHeaders(headersList);
 *   
 *   return <EllmoApi clientId="your-id" url={pathname} />;
 * }
 * ```
 */
export function getPathnameFromHeaders(headers: { get(name: string): string | null }): string | null {
  return headers.get(ELLMO_PATHNAME_HEADER);
}

export default ellmoMiddleware;
